import { useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { FaArrowUp, FaArrowDown } from 'react-icons/fa'
import { getNotaSpese } from '../../features/notaSpese/notaSpeseSlice'
import { getAllCategories } from '../../features/categorie/categorieSlice'
import { getCategoryName, getSignedImporto, isEntrata } from '../shared/utils/helpers'
import { parseDate } from '../utils/dateParser'
import Chart from '../charts/Chart'

// No budget-limit concept exists in the data model yet — example value only.
const MONTHLY_BUDGET_PLACEHOLDER = 1500

const stringToColor = (string) => {
  let hash = 0
  for (let i = 0; i < (string?.length || 0); i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = Math.abs(hash) % 360
  return `hsl(${hue}, 55%, 48%)`
}

const isInMonth = (dateString, month, year) => {
  const d = new Date(dateString)
  return d.getMonth() === month && d.getFullYear() === year
}

function DashboardUser() {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const { notaSpese } = useSelector((state) => state.notaSpese)
  const { categorie, isLoading: categorieLoading } = useSelector((state) => state.categorie)

  useEffect(() => {
    dispatch(getNotaSpese())
  }, [dispatch])

  useEffect(() => {
    if (categorie.length === 0 && !categorieLoading) {
      dispatch(getAllCategories())
    }
  }, [categorie.length, categorieLoading, dispatch])

  const now = new Date()
  const thisMonth = now.getMonth()
  const thisYear = now.getFullYear()
  const lastMonthDate = new Date(thisYear, thisMonth - 1, 1)
  const lastMonth = lastMonthDate.getMonth()
  const lastMonthYear = lastMonthDate.getFullYear()

  const thisMonthNote = useMemo(
    () => notaSpese.filter((n) => isInMonth(n.inserimentoData, thisMonth, thisYear)),
    [notaSpese, thisMonth, thisYear]
  )
  const lastMonthNote = useMemo(
    () => notaSpese.filter((n) => isInMonth(n.inserimentoData, lastMonth, lastMonthYear)),
    [notaSpese, lastMonth, lastMonthYear]
  )

  const totalSpentThisMonth = useMemo(
    () => thisMonthNote.reduce((sum, n) => sum + getSignedImporto(n), 0),
    [thisMonthNote]
  )
  const totalSpentLastMonth = useMemo(
    () => lastMonthNote.reduce((sum, n) => sum + getSignedImporto(n), 0),
    [lastMonthNote]
  )

  const trend = useMemo(() => {
    if (totalSpentLastMonth === 0) {
      return totalSpentThisMonth > 0 ? { label: 'New this month', direction: null } : null
    }
    const pct = ((totalSpentThisMonth - totalSpentLastMonth) / totalSpentLastMonth) * 100
    return {
      label: `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% vs last month`,
      direction: pct >= 0 ? 'up' : 'down',
    }
  }, [totalSpentThisMonth, totalSpentLastMonth])

  const remaining = MONTHLY_BUDGET_PLACEHOLDER - totalSpentThisMonth
  const isOverBudget = totalSpentThisMonth > MONTHLY_BUDGET_PLACEHOLDER
  const pctOfLimit = (totalSpentThisMonth / MONTHLY_BUDGET_PLACEHOLDER) * 100
  const pctOfLimitCapped = Math.min(100, Math.max(0, pctOfLimit))

  const categoryBreakdown = useMemo(() => {
    const totals = {}
    thisMonthNote.filter((n) => !isEntrata(n)).forEach((n) => {
      const name = getCategoryName(categorie, n.categoria)
      totals[name] = (totals[name] || 0) + (n.importo || 0)
    })
    return Object.entries(totals)
      .map(([name, total]) => ({ name, total, color: stringToColor(name) }))
      .sort((a, b) => b.total - a.total)
  }, [thisMonthNote, categorie])

  const topCategory = categoryBreakdown[0] || null

  const recentTransactions = useMemo(
    () =>
      [...notaSpese]
        .sort((a, b) => new Date(b.inserimentoData) - new Date(a.inserimentoData))
        .slice(0, 5),
    [notaSpese]
  )

  const doughnutData = {
    labels: categoryBreakdown.map((c) => c.name),
    datasets: [
      {
        data: categoryBreakdown.map((c) => c.total),
        backgroundColor: categoryBreakdown.map((c) => c.color),
        borderWidth: 0,
      },
    ],
  }

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '72%',
    plugins: {
      legend: { display: false },
    },
  }

  if (!notaSpese || notaSpese.length === 0) {
    return (
      <div className="container py-2">
        <div className="row">
          <h3 className="mb-0 h4 font-weight-bolder">Dashboard</h3>
          <p className="mb-4">Explore your financial data with our dashboard.</p>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body p-4 text-center">
                <h5 className="mb-2">No expenses recorded yet</h5>
                <p className="text-sm mb-3">
                  Add your first expense to see your dashboard come to life.
                </p>
                <Link to="/note-spese/shared" className="btn bg-dark text-white">
                  Expense list
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container py-2">
      <div className="row">
        <h3 className="mb-0 h4 font-weight-bolder">Dashboard</h3>
        <p className="mb-4">
          Welcome back{user?.name ? `, ${user.name}` : ''}. Here's your monthly overview.
        </p>
      </div>

      <div className="row">
        <div className="col-lg-8 col-md-12 mb-4">
          <div className="card h-100">
            <div className="card-body">
              <p className="text-uppercase text-xs font-weight-bolder text-secondary mb-1">
                Monthly Overview
              </p>
              <div className="d-flex align-items-baseline flex-wrap gap-2">
                <h2 className="mb-0 font-weight-bolder">€ {totalSpentThisMonth.toFixed(2)}</h2>
                {trend && (
                  <span
                    className={`text-sm font-weight-bold ${
                      trend.direction === 'down'
                        ? 'text-success'
                        : trend.direction === 'up'
                        ? 'text-danger'
                        : 'text-secondary'
                    }`}
                  >
                    {trend.direction === 'up' && <FaArrowUp className="me-1" />}
                    {trend.direction === 'down' && <FaArrowDown className="me-1" />}
                    {trend.label}
                  </span>
                )}
              </div>
              <p className="text-sm text-secondary mb-4">Total spent this month</p>

              <div className="d-flex gap-5 mb-2">
                <div>
                  <p className="text-xs text-secondary mb-1">
                    Monthly Budget <span className="text-xxs">(example)</span>
                  </p>
                  <p className="mb-0 font-weight-bold">
                    € {MONTHLY_BUDGET_PLACEHOLDER.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-secondary mb-1">
                    {isOverBudget ? 'Over budget by' : 'Remaining'}
                  </p>
                  <p className={`mb-0 font-weight-bold ${isOverBudget ? 'text-danger' : ''}`}>
                    € {Math.abs(remaining).toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="progress-wrapper">
                <div className="progress">
                  <div
                    className={`progress-bar ${isOverBudget ? 'bg-gradient-danger' : 'bg-gradient-info'}`}
                    role="progressbar"
                    style={{ width: `${pctOfLimitCapped}%` }}
                    aria-valuenow={Math.round(pctOfLimit)}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
                <p className="text-end text-xs text-secondary mt-1 mb-0">
                  {pctOfLimit.toFixed(1)}% of limit
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-4 col-md-12 mb-4">
          <div className="card h-100">
            <div className="card-header p-2 ps-3">
              <p className="text-sm mb-0">Top Category</p>
              <h4 className="mb-0">{topCategory ? topCategory.name : '—'}</h4>
            </div>
            <div className="card-footer p-2 ps-3">
              <p className="mb-0 text-sm">
                {topCategory ? (
                  <>
                    <span className="font-weight-bolder">€ {topCategory.total.toFixed(2)}</span> spent
                    this month
                  </>
                ) : (
                  'No spending yet this month'
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-4 col-md-6 mb-4">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="mb-4">Allocation</h6>
              {categoryBreakdown.length > 0 ? (
                <>
                  <div
                    className="position-relative mx-auto mb-4"
                    style={{ width: 180, height: 180 }}
                  >
                    <Chart type="doughnut" data={doughnutData} options={doughnutOptions} />
                    <div className="position-absolute top-50 start-50 translate-middle text-center">
                      <p className="mb-0 font-weight-bolder">€ {totalSpentThisMonth.toFixed(0)}</p>
                      <p className="mb-0 text-xxs text-uppercase text-secondary">Total</p>
                    </div>
                  </div>
                  <div>
                    {categoryBreakdown.slice(0, 5).map((c) => (
                      <div
                        className="d-flex justify-content-between align-items-center mb-2"
                        key={c.name}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: c.color,
                              display: 'inline-block',
                            }}
                          ></span>
                          <span className="text-sm">{c.name}</span>
                        </div>
                        <span className="text-sm font-weight-bold">€ {c.total.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-secondary">No spending yet this month</p>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-8 col-md-6 mb-4">
          <div className="card h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h6 className="mb-0">Ledger Activity</h6>
                <Link to="/note-spese/shared" className="text-xs font-weight-bold text-uppercase">
                  View All
                </Link>
              </div>
              {recentTransactions.map((n) => {
                const categoryName = getCategoryName(categorie, n.categoria)
                return (
                  <div
                    className="d-flex justify-content-between align-items-center py-2"
                    key={n._id}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <span
                        className="d-flex align-items-center justify-content-center text-white font-weight-bold"
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: '0.75rem',
                          backgroundColor: stringToColor(categoryName),
                          flexShrink: 0,
                        }}
                      >
                        {categoryName.charAt(0).toUpperCase()}
                      </span>
                      <div>
                        <p className="mb-0 font-weight-bold text-sm">{n.testo}</p>
                        <p className="mb-0 text-xs text-secondary">
                          {parseDate(n.inserimentoData)} • {categoryName}
                        </p>
                      </div>
                    </div>
                    <p className={`mb-0 font-weight-bold ${isEntrata(n) ? 'nota-spesa-amount--income' : ''}`}>
                      {isEntrata(n) ? '+' : '-'} € {(n.importo || 0).toFixed(2)}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardUser
